/**
 * RiskDashboardAgGridHook
 *
 * AG Grid hook for the Risk Management Supervisor Dashboard.
 *
 * Replaces a plain HTML table whose Actions column depended on horizontal
 * page scroll to reach (see supervisor_dashboard.html.heex history) — AG
 * Grid pins the Transaction column left and Actions right, so the row's
 * identity and its buttons are always on screen while the middle columns
 * (Amount, Card/Payment, Rule, RRN/TID, Merchant) scroll independently.
 * The Columns side panel also lets a supervisor hide/show any of those
 * middle columns by hand, mirroring CoreTransactionAgGridHook.
 *
 * Server-side pagination/filtering stays exactly as it was — this hook only
 * renders whichever page of rows the LiveView already computed into
 * @paginated_transactions; it does not paginate on its own.
 *
 * LiveView events pushed (via pushEvent):
 *  "take_action"        – { hit_id, action }  (Request Docs / Release)
 *  "open_decision"       – { hit_id, action }  (Approve / Decline prompt)
 *  "select_transaction"  – { hit_id }          (View Details slide-over)
 */

// Tailwind utility classes, not inline hex — so these inherit the app's
// `.theme-dark` overrides (theme.css) automatically instead of staying
// baked-in light-mode colors that go illegible on a dark row background.
const STATUS_CLASSES = {
  Hold: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10",
  Alert: "bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/20",
  "Request Docs": "bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20",
  Approved: "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20",
  Declined: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20",
  Released: "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20",
};
const DEFAULT_STATUS_CLASSES = "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10";

const statusBadge = (params) => {
  const value = params.value;
  if (!value) return "";
  const classes = STATUS_CLASSES[value] || DEFAULT_STATUS_CLASSES;
  return `<span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}">${value}</span>`;
};

const AGE_CLASSES = {
  green: "text-green-600 bg-green-50",
  yellow: "text-yellow-600 bg-yellow-50",
  orange: "text-orange-600 bg-orange-50",
  red: "text-red-600 bg-red-50",
  gray: "text-gray-500 bg-gray-50",
};

const ageTier = (hoursOld, status) => {
  if (status !== "Hold") return "gray";
  if (hoursOld < 24) return "green";
  const days = Math.floor(hoursOld / 24);
  if (days <= 7) return "yellow";
  if (days <= 14) return "orange";
  return "red";
};

const ageBadge = (params) => {
  const { hours_old, status, age_label } = params.data || {};
  const classes = AGE_CLASSES[ageTier(hours_old, status)];
  return `<span class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold ${classes}">⏱ ${age_label}</span>`;
};

const transactionCell = (params) => {
  const { transaction_id, transaction_type } = params.data || {};
  return `<div class="py-1 leading-tight">
    <div class="text-sm font-medium text-gray-900">#${transaction_id}</div>
    <div class="text-xs text-gray-500">${transaction_type || ""}</div>
  </div>`;
};

const amountCell = (params) => {
  const { amount, currency } = params.data || {};
  if (amount === null || amount === undefined) return `<span class="text-xs text-gray-400">N/A</span>`;
  const formatted = Number(amount).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `<span class="text-sm font-semibold text-gray-900">${currency || "AED"} ${formatted}</span>`;
};

const cardCell = (params) => {
  const { transaction_type, card_number_masked, card_type, payment_mode } = params.data || {};
  if (transaction_type === "POS") {
    if (card_number_masked) {
      return `<div class="py-1 leading-tight">
        <div class="text-sm font-medium text-gray-900">${card_number_masked}</div>
        ${card_type ? `<div class="text-xs text-gray-500">${card_type.charAt(0).toUpperCase()}${card_type.slice(1)}</div>` : ""}
      </div>`;
    }
    return `<span class="text-xs text-gray-500">POS</span>`;
  }
  return `<span class="text-xs text-gray-500">${payment_mode || "QR Payment"}</span>`;
};

const merchantCell = (params) => {
  const { merchant_id, category } = params.data || {};
  return `<div class="py-1 leading-tight">
    <div class="text-sm text-gray-900">${merchant_id || ""}</div>
    <div class="text-xs text-gray-500">Category: ${category || ""}</div>
  </div>`;
};

const rrnTidCell = (params) => {
  const { rrn, tid } = params.data || {};
  return `<div class="py-1 leading-tight">
    <div class="text-xs text-gray-900">RRN: ${rrn}</div>
    <div class="text-xs text-gray-500">TID: ${tid}</div>
  </div>`;
};

const RiskDashboardAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");
    const hook = this;

    // Tailwind classes (not inline hex) so hover/focus and dark mode all
    // ride the same CSS this app already ships for its plain <button>s.
    const iconBtn = (icon, colourClasses, title, onClick) => {
      const btn = document.createElement("button");
      btn.title = title;
      btn.innerHTML = icon;
      btn.className = `inline-flex items-center justify-center w-7 h-7 mr-1 rounded-md border shadow-sm transition-colors duration-150 ${colourClasses}`;
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        onClick();
      });
      return btn;
    };

    const ICONS = {
      doc: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>',
      eye: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>',
      check: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>',
      x: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>',
      arrowRight: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 12h8M12 8l4 4-4 4"/></svg>',
    };

    const COLOURS = {
      yellow: "text-yellow-600 hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 border-yellow-200 hover:border-yellow-300",
      indigo: "text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-indigo-200 hover:border-indigo-300",
      green: "text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300",
      red: "text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border-red-200 hover:border-red-300",
      emerald: "text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-200 hover:border-emerald-300",
    };

    const actionsRenderer = (params) => {
      const data = params.data;
      if (!data) return "";
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;justify-content:flex-end;padding:2px 0;";
      const status = String(data.status || "").toLowerCase();
      const id = String(data.id);

      const viewBtn = () =>
        iconBtn(ICONS.eye, COLOURS.indigo, "View Details", () =>
          hook.pushEvent("select_transaction", { hit_id: id })
        );

      if (status === "hold") {
        wrap.appendChild(
          iconBtn(ICONS.doc, COLOURS.yellow, "Request Docs", () =>
            hook.pushEvent("take_action", { hit_id: id, action: "Request Docs" })
          )
        );
        wrap.appendChild(viewBtn());
      } else if (status === "request docs") {
        wrap.appendChild(
          iconBtn(ICONS.check, COLOURS.green, "Approve", () =>
            hook.pushEvent("open_decision", { hit_id: id, action: "Approve" })
          )
        );
        wrap.appendChild(
          iconBtn(ICONS.x, COLOURS.red, "Decline", () =>
            hook.pushEvent("open_decision", { hit_id: id, action: "Decline" })
          )
        );
        wrap.appendChild(viewBtn());
      } else if (status === "approved") {
        wrap.appendChild(
          iconBtn(ICONS.arrowRight, COLOURS.emerald, "Release into settlement", () =>
            hook.pushEvent("take_action", { hit_id: id, action: "Release" })
          )
        );
        wrap.appendChild(viewBtn());
      } else {
        wrap.appendChild(viewBtn());
      }

      return wrap;
    };

    const columnDefs = [
      {
        headerName: "Transaction",
        field: "transaction_id",
        colId: "transaction",
        pinned: "left",
        width: 140,
        cellRenderer: transactionCell,
        filter: "agTextColumnFilter",
      },
      { headerName: "Amount", field: "amount", flex: 1, minWidth: 120, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "Card / Payment", field: "card_number_masked", flex: 1.1, minWidth: 150, cellRenderer: cardCell },
      {
        headerName: "Rule Triggered",
        field: "rule_name",
        flex: 1.4,
        minWidth: 180,
        tooltipField: "rule_name",
        cellStyle: { fontSize: "12px" },
      },
      { headerName: "Merchant", field: "merchant_id", flex: 1, minWidth: 140, cellRenderer: merchantCell },
      { headerName: "RRN / TID", field: "rrn", flex: 1, minWidth: 140, cellRenderer: rrnTidCell },
      { headerName: "Status", field: "status", flex: 0.9, minWidth: 120, cellRenderer: statusBadge },
      { headerName: "Age", field: "hours_old", flex: 0.7, minWidth: 90, cellRenderer: ageBadge, type: "numericColumn" },
      {
        headerName: "Actions",
        field: "id",
        colId: "actions",
        pinned: "right",
        width: 130,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: actionsRenderer,
      },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: false,
        minWidth: 90,
      },
      animateRows: true,
      rowHeight: 52,
      headerHeight: 40,
      suppressPaginationPanel: true,
      pagination: false,
      popupParent: document.body,
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
        ],
        defaultToolPanel: "",
      },
      overlayNoRowsTemplate: `<span class="text-sm text-gray-500">No flagged transactions.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("RiskDashboardAgGridHook: agGrid not found on window");
    }
  },

  // phx-update="ignore" keeps LiveView from patching this element's
  // children, but it still patches the element's own attributes — so
  // data-rows carries fresh rows after every filter/search/paginate, and
  // this fallback (matching CoreTransactionAgGridHook) picks them up.
  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        const rows = JSON.parse(this.el.dataset.rows);
        this.gridApi.setGridOption("rowData", rows);
      } catch (e) {
        console.warn("RiskDashboardAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default RiskDashboardAgGridHook;
